import type { ComputedRef, Ref } from 'vue';
import type { BaseModel } from '@/_api/models/base';
import type { BaseService } from '@/_api/services/base';
import type {
  FilterConfig,
  FilterCategoryConfig,
  FilterState,
  FilterOptionsSetup,
} from '@/_api/types/interfaces/filter';
import { useCustomFieldsStore } from '~/stores/useCustomFieldsStore';

export interface FilterControllerOptions<T extends BaseModel> {
  /** The service instance used for remote filter requests */
  service?: BaseService<T>;

  /** Filter definitions */
  filters: Record<string, FilterConfig<T>>;

  /** Filter categories for UI grouping */
  categories?: FilterCategoryConfig[];

  /** Named category sets for multiple category arrays (e.g., { marketing: [...] }) */
  categorySets?: Record<string, FilterCategoryConfig[]>;

  /** Whether all filters must match (AND) or any (OR). Default: true (AND) */
  matchAll?: boolean;

  /** Async option loaders that populate filter options from stores/API */
  filterOptions?: FilterOptionsSetup[];

  /** Enable dynamic custom field filters from useCustomFieldsStore */
  customFields?: { modelName: string };
}

export interface FilterControllerReturn<T extends BaseModel> {
  /** Reactive filter states keyed by filter name (includes config display properties) */
  filterStates: Ref<Record<string, FilterState>>;

  /** The filter configs (for advanced use) */
  filterConfigs: Record<string, FilterConfig<T>>;

  /** Categories with custom field keys resolved */
  categories: ComputedRef<FilterCategoryConfig[]>;

  /** Named category sets with custom field keys resolved */
  categorySets: ComputedRef<Record<string, FilterCategoryConfig[]>>;

  /** Check if any filters are active (have a valueLabel) */
  isActive: ComputedRef<boolean>;

  /** Check if all active filters are valid */
  isValid: ComputedRef<boolean>;

  /** Check if no filters are set */
  isEmpty: ComputedRef<boolean>;

  /** Whether all filters must match (AND) or any (OR) */
  matchAll: Ref<boolean>;

  /** Check if a specific filter is empty */
  isFilterEmpty: (key: string) => boolean;

  /** Get display label for a filter's current value */
  getValueLabel: (key: string) => string | null;

  /** Get validation result for a filter's current value */
  getValidation: (key: string) => { status: boolean; message: string } | null;

  /** Clear all filters */
  clear: () => void;

  /** Clear a specific filter */
  clearFilter: (key: string) => void;

  /** Get the current value for a filter */
  getValue: (key: string) => any;

  /** Set a value for a filter */
  setValue: (key: string, value: any) => void;

  /** Apply local filters against an item list */
  filterLocal: (items: T[]) => T[];

  /** Apply remote + local filters (full pipeline) */
  filter: (items: T[]) => Promise<T[]>;

  /** Collect all non-empty filter params as Record<type, params> for remote calls */
  getFilterParams: () => Record<string, any>;

  /** Load all filter options */
  loadOptions: () => Promise<void>;

  /** Update a specific filter option loader by index */
  updateOptions: (index: number) => Promise<void>;

  /** The filter option setup entries */
  filterOptions: FilterOptionsSetup[];

  /** Keys of dynamically-added custom field filters */
  customFilterKeys: Ref<string[]>;

  /** Get active filter count for a category */
  categoryActiveCount: (category: FilterCategoryConfig) => number;

  /** Serialize active filter state to a plain object */
  toJSON: () => Record<string, any>;

  /** Restore filter state from a plain object */
  fromJSON: (data: Record<string, any>) => void;
}

export const useFilterController = <T extends BaseModel>(
  options: FilterControllerOptions<T>
): FilterControllerReturn<T> => {
  const {
    filters: initialFilterConfigs,
    categories: categoriesConfig = [],
    categorySets: categorySetsConfig = {},
    matchAll: initialMatchAll = true,
    filterOptions: filterOptionsConfig = [],
    customFields: customFieldsConfig,
  } = options;

  // Make filterConfigs mutable for custom field additions
  const filterConfigs: Record<string, FilterConfig<T>> = { ...initialFilterConfigs };

  // matchAll as a ref for v-model binding
  const matchAllRef = ref(initialMatchAll);

  // Track custom field filter keys
  const customFilterKeys = ref<string[]>([]);

  // Initialize reactive filter states with merged config display properties
  const filterStates = ref<Record<string, FilterState>>({}) as Ref<Record<string, FilterState>>;

  for (const [key, config] of Object.entries(filterConfigs)) {
    filterStates.value[key] = {
      value: config.defaultValue,
      items: null,
      isLoading: false,
      options: config.options ?? [],
      disabled: config.disabled ?? false,
      label: config.label,
      type: config.type,
      kind: config.kind,
      icon: config.icon,
      strategy: config.strategy,
      component: config.component,
      props: config.props,
      info: config.info,
      tooltip: config.tooltip,
      features: config.features,
      custom: config.custom,
      optionCount: config.optionCount,
    };
  }

  // --- Core helpers ---

  const getValue = (key: string): any => {
    return filterStates.value[key]?.value;
  };

  const setValue = (key: string, value: any): void => {
    if (filterStates.value[key]) {
      filterStates.value[key].value = value;
      filterStates.value[key].items = null;
    }
  };

  const isFilterEmpty = (key: string): boolean => {
    const config = filterConfigs[key];
    const state = filterStates.value[key];

    if (!config || !state) return true;

    return config.isEmpty(state.value);
  };

  const getValueLabel = (key: string): string | null => {
    const config = filterConfigs[key];
    const state = filterStates.value[key];
    if (!config || !state) return null;
    return config.valueLabel?.(state.value) ?? null;
  };

  const getValidation = (key: string): { status: boolean; message: string } | null => {
    const config = filterConfigs[key];
    const state = filterStates.value[key];
    if (!config || !state) return null;
    return config.validate?.(state.value) ?? null;
  };

  const isEmpty = computed(() => {
    for (const key in filterConfigs) {
      if (!isFilterEmpty(key)) return false;
    }
    return true;
  });

  const isActive = computed(() => {
    for (const key in filterConfigs) {
      if (!isFilterEmpty(key)) {
        const config = filterConfigs[key];
        if (config?.valueLabel?.(filterStates.value[key]?.value)) {
          return true;
        }
      }
    }
    return false;
  });

  const isValid = computed(() => {
    for (const key in filterConfigs) {
      if (!isFilterEmpty(key)) {
        const config = filterConfigs[key];
        if (config?.validate) {
          const error = config.validate(filterStates.value[key]?.value);
          if (error) return false;
        }
      }
    }
    return true;
  });

  const clear = (): void => {
    for (const key in filterConfigs) {
      clearFilter(key);
    }
  };

  const clearFilter = (key: string): void => {
    const config = filterConfigs[key];
    if (config && filterStates.value[key]) {
      filterStates.value[key].value =
        typeof config.defaultValue === 'object' && config.defaultValue !== null
          ? JSON.parse(JSON.stringify(config.defaultValue))
          : config.defaultValue;
      filterStates.value[key].items = null;
    }
  };

  // --- Categories ---

  const resolveCategories = (cats: FilterCategoryConfig[]): FilterCategoryConfig[] => {
    return cats.map(cat => {
      if (cat.features?.includes('custom-fields') && customFilterKeys.value.length) {
        return { ...cat, filters: [...cat.filters, ...customFilterKeys.value] };
      }
      return cat;
    });
  };

  const categories = computed<FilterCategoryConfig[]>(() => {
    return resolveCategories(categoriesConfig);
  });

  const categorySetsComputed = computed<Record<string, FilterCategoryConfig[]>>(() => {
    const result: Record<string, FilterCategoryConfig[]> = {};
    for (const [name, configs] of Object.entries(categorySetsConfig)) {
      result[name] = resolveCategories(configs);
    }
    return result;
  });

  // --- Filter Options ---

  const loadOptions = async (): Promise<void> => {
    for (const opt of filterOptionsConfig) {
      try {
        const result = await opt.map();
        opt.set(result);
      } catch {
        // Continue loading other options
      }
    }
  };

  const updateOptions = async (index: number): Promise<void> => {
    const opt = filterOptionsConfig[index];
    if (opt) {
      try {
        const result = await opt.map();
        opt.set(result);
      } catch {
        // Continue with current options
      }
    }
  };

  // --- Custom Fields ---

  if (customFieldsConfig) {
    const customFieldsStore = useCustomFieldsStore();

    watch(
      () => customFieldsStore[customFieldsConfig.modelName],
      (fields: any[]) => {
        if (!fields) return;

        const newKeys: string[] = [];
        for (const field of fields) {
          const id = field.idCustomField;
          newKeys.push(id);

          filterConfigs[id] = {
            type: 'customField',
            label: field.name,
            strategy: 'remote',
            component: 'OCustomField',
            custom: field,
            defaultValue: null,
            isEmpty: (value: any) => value === null,
            valueLabel: (value: any) => value,
            getParams: (value: any) => ({
              idCustomField: field.idCustomField,
              value,
            }),
          } as FilterConfig<T>;

          if (!filterStates.value[id]) {
            filterStates.value[id] = {
              value: null,
              items: null,
              isLoading: false,
              options: [],
              disabled: false,
              label: field.name,
              type: 'customField',
              strategy: 'remote',
              component: 'OCustomField',
              custom: field,
            };
          }
        }

        customFilterKeys.value = newKeys;
      },
      { immediate: true }
    );

    customFieldsStore.sync();
  }

  // --- Filter execution ---

  const filterLocal = (items: T[]): T[] => {
    const filterResults: Record<string, T[]> = {};

    for (const key in filterConfigs) {
      const config = filterConfigs[key];
      const state = filterStates.value[key];
      if (!config || !state) continue;

      if (!isFilterEmpty(key) && config.matches) {
        const shouldFilterLocally =
          config.strategy === 'local' ||
          (config.strategy === 'hybrid' && config.filterLocally?.(state.value));

        if (shouldFilterLocally) {
          filterResults[key] = config.matches(items, state.value);
        }
      }
    }

    const resultSets = Object.values(filterResults);

    if (resultSets.length === 0) return items;

    if (matchAllRef.value && resultSets.length > 1) {
      const idSets = resultSets.map(
        set => new Set(set.map(item => item.objectID()))
      );

      return (resultSets[0] ?? []).filter(item => {
        const id = item.objectID();
        return idSets.every(set => set.has(id));
      });
    }

    const seen = new Set<string | null>();
    const result: T[] = [];

    for (const set of resultSets) {
      for (const item of set) {
        const id = item.objectID();
        if (!seen.has(id)) {
          seen.add(id);
          result.push(item);
        }
      }
    }

    return result;
  };

  const filter = async (items: T[]): Promise<T[]> => {
    if (!isValid.value) return [];

    // Collect remote filter requests
    const remoteRequests: { filter: string; params: any }[] = [];
    const remoteKeys: string[] = [];

    for (const key in filterConfigs) {
      const config = filterConfigs[key];
      const state = filterStates.value[key];
      if (!config || !state) continue;

      if (!isFilterEmpty(key)) {
        const shouldFilterRemotely =
          config.strategy === 'remote' ||
          (config.strategy === 'hybrid' && config.filterRemotely?.(state.value));

        if (shouldFilterRemotely && config.getParams) {
          remoteRequests.push({
            filter: config.type,
            params: config.getParams(state.value),
          });
          remoteKeys.push(key);
          state.isLoading = true;
        }
      }
    }

    // Build lookup of all items
    const allResults: Record<string, T> = {};
    for (const item of items) {
      const id = item.objectID();
      if (id) allResults[id] = item;
    }

    // Execute remote filters if needed
    let remoteResponses: Record<string, any> | null = null;

    if (remoteRequests.length > 0 && options.service) {
      try {
        const response = await (options.service as any).filter(
          remoteRequests,
          {}
        );

        remoteResponses = {};
        if (response?.filters) {
          for (const filterResponse of response.filters) {
            remoteResponses[filterResponse.filter] = filterResponse;

            const key = remoteKeys.find(k => filterConfigs[k]?.type === filterResponse.filter);
            if (key) {
              const config = filterConfigs[key];
              config?.setParams?.(filterStates.value[key]?.value, filterResponse.params);
            }
          }
        }
      } finally {
        for (const key of remoteKeys) {
          if (filterStates.value[key]) {
            filterStates.value[key].isLoading = false;
          }
        }
      }

      // Fetch missing remote items
      if (remoteResponses) {
        const allMissingIds: string[] = [];
        for (const filterResponse of Object.values(remoteResponses)) {
          if (filterResponse.ids) {
            for (const id of filterResponse.ids) {
              if (!allResults[id] && !allMissingIds.includes(id)) {
                allMissingIds.push(id);
              }
            }
          }
        }

        if (allMissingIds.length > 0 && options.service) {
          try {
            const remoteItems: T[] = await (options.service as any).listIDs(allMissingIds);
            if (remoteItems) {
              for (const item of remoteItems) {
                const id = item.objectID();
                if (id) allResults[id] = item;
              }
            }
          } catch {
            // Continue with available items
          }
        }
      }
    }

    // Apply all filters and collect matched items
    const filterResults: Record<string, T[]> = {};
    const allItems = Object.values(allResults);

    for (const key in filterConfigs) {
      const config = filterConfigs[key];
      const state = filterStates.value[key];
      if (!config || !state) continue;

      if (!isFilterEmpty(key)) {
        if (config.strategy === 'remote') {
          if (remoteResponses?.[config.type]?.ids) {
            state.items = remoteResponses[config.type].ids
              .map((id: string) => allResults[id])
              .filter(Boolean);
            filterResults[key] = state.items as T[];
          }
        } else if (config.strategy === 'hybrid') {
          if (config.filterLocally?.(state.value) && config.matches) {
            state.items = config.matches(allItems, state.value);
            filterResults[key] = state.items as T[];
          } else if (config.filterRemotely?.(state.value) && remoteResponses?.[config.type]?.ids) {
            state.items = remoteResponses[config.type].ids
              .map((id: string) => allResults[id])
              .filter(Boolean);
            filterResults[key] = state.items as T[];
          }
        } else if (config.matches) {
          state.items = config.matches(allItems, state.value);
          filterResults[key] = state.items as T[];
        }
      }
    }

    const resultSets = Object.values(filterResults);

    if (resultSets.length === 0) return items;

    if (matchAllRef.value && resultSets.length > 1) {
      const idSets = resultSets.map(
        set => new Set(set.map(item => item.objectID()))
      );

      return (resultSets[0] ?? []).filter(item => {
        const id = item.objectID();
        return idSets.every(set => set.has(id));
      });
    }

    const seen = new Set<string | null>();
    const result: T[] = [];

    for (const set of resultSets) {
      for (const item of set) {
        const id = item.objectID();
        if (!seen.has(id)) {
          seen.add(id);
          result.push(item);
        }
      }
    }

    return result;
  };

  // --- Param collection ---

  const getFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {};
    for (const key in filterConfigs) {
      const config = filterConfigs[key];
      const state = filterStates.value[key];
      if (!config || !state) continue;
      if (!isFilterEmpty(key) && config.getParams) {
        params[config.type] = config.getParams(state.value);
      }
    }
    return params;
  };

  // --- Serialization ---

  const categoryActiveCount = (category: FilterCategoryConfig): number => {
    let count = 0;
    for (const key of category.filters) {
      if (!isFilterEmpty(key)) count++;
    }
    return count;
  };

  const toJSON = (): Record<string, any> => {
    const data: Record<string, any> = {};

    for (const key in filterConfigs) {
      const config = filterConfigs[key];
      const state = filterStates.value[key];

      if (!isFilterEmpty(key) && config?.valueLabel?.(state?.value)) {
        data[key] = state?.value;
      }
    }

    return data;
  };

  const fromJSON = (data: Record<string, any>): void => {
    for (const key in data) {
      if (filterConfigs[key] && filterStates.value[key]) {
        filterStates.value[key].value = data[key];
      }
    }
  };

  return {
    filterStates,
    filterConfigs,
    categories,
    categorySets: categorySetsComputed,
    isActive,
    isValid,
    isEmpty,
    matchAll: matchAllRef,
    isFilterEmpty,
    getValueLabel,
    getValidation,
    clear,
    clearFilter,
    getValue,
    setValue,
    filterLocal,
    filter,
    loadOptions,
    updateOptions,
    filterOptions: filterOptionsConfig,
    customFilterKeys,
    getFilterParams,
    categoryActiveCount,
    toJSON,
    fromJSON,
  };
};
